import React, { useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';

import Grid from '@mui/material/Grid';

import Dialog, { useDialog } from '@ts-core/components/Dialog';
import ToMainMenuButton from '@ts-core/components/ToMainMenuButton';

import StudentsSelector, { useStudentsSelector } from 'modules/students/components/StudentsSelector';
import { useStudents, useGroupStudents } from 'modules/students/hooks';
import { useGroup } from 'modules/groups/hooks';

import GroupsPanel, { useGroupsPanel } from '../../components/GroupsPanel';
import GroupStudentsTable from '../../components/GroupStudentsTable';

const GroupDetailsPage = () => {
  const history = useHistory();
  const intl = useIntl();
  const { courseId, groupId } = useParams();

  const { onCloseDialog, onOpenDialog, dialogProps } = useDialog();

  const {
    students,
    loading: loadingStudents,
    refetch: refetchStudents,
  } = useStudents({
    courseId,
  });

  const navigateToGroup = useCallback(
    (group) => {
      history.push(`/${courseId}/groups/${group._id}`);
    },
    [history, courseId]
  );

  const handleStudentsAddedToGroup = useCallback(() => {
    onCloseDialog();
    refetchStudents();
  }, [onCloseDialog, refetchStudents]);

  const { group, onAddStudentsToGroup, addingStudentsToGroup } = useGroup({
    courseId,
    groupId,
    onStudentsAddedToGroup: handleStudentsAddedToGroup,
  });

  const { studentsNotInGroup, groupStudents } = useGroupStudents({
    students,
    groupId,
  });

  const handleAddStudentsToGroup = useCallback(
    (selectedStudents) => {
      onAddStudentsToGroup(selectedStudents);
    },
    [onAddStudentsToGroup]
  );

  const handleOpenStudentsSelector = useCallback(() => {
    onOpenDialog({
      title: intl.formatMessage({ id: 'groupsDetails.addStudents' }),
    });
  }, [onOpenDialog, intl]);

  const studentsSelectorProps = useStudentsSelector({
    students: studentsNotInGroup,
    loading: loadingStudents,
    submitting: addingStudentsToGroup,
    onConfirmSelection: handleAddStudentsToGroup,
  });

  const groupsPanelProps = useGroupsPanel({
    courseId,
    onSelectGroup: navigateToGroup,
    selectedGroupId: groupId,
    showSelectedMark: true,
  });

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ToMainMenuButton noMargin />
        </Grid>
        <Grid item xs={12} md={4}>
          <GroupsPanel {...groupsPanelProps} />
        </Grid>
        <Grid item xs={12} md={8}>
          <GroupStudentsTable
            onAddStudents={handleOpenStudentsSelector}
            addStudentsEnabled={studentsNotInGroup.length > 0}
            groupName={group?.name}
            students={groupStudents}
            loading={loadingStudents}
          />
        </Grid>
      </Grid>
      <Dialog {...dialogProps}>{() => <StudentsSelector {...studentsSelectorProps} />}</Dialog>
    </>
  );
};

export default GroupDetailsPage;
